/**
 * The Authentication sections: their ids, labels, which tenant-settings
 * fields each owns, and which of them apply to a tenant.
 *
 * GATING IS THE CARDS' GATING, UNCHANGED. Every predicate below is the exact
 * condition under which the card it replaces rendered on the old single-page
 * Sign-in tab (tenantCapabilities() plus the same audience and permission
 * checks). A section with nothing to show is absent from the nav — never
 * shown disabled — and its route redirects to the Overview.
 *
 * NOTHING UNWIRED IS OFFERED. There is no Sign-in page section: of what it
 * held, only the badge button changes the end-user sign-in page (now in
 * Methods), the support contact and help link appear when a recovery step
 * fails (now in Recovery), and the phone option title appears when someone
 * adds a passkey (now in Passkeys). The passkey button and its text, the
 * suggested first option, the option order and three wording slots reach no
 * one and are gone; see the UNWIRED notes in the backend.
 */

import type { TenantCapabilities } from "@/features/tenants/capabilities";
import type { TenantDetail } from "@/features/tenants/schemas";
import type { AuthenticationSettings } from "@/features/tenant-settings/schemas";

export type SignInSectionId =
  | "overview"
  | "methods"
  | "passkeys"
  | "phone"
  | "recovery"
  | "sessions";
export type AuthenticatorsSectionId = "models" | "add-model" | "checks";
export type SettingsSectionId = SignInSectionId | AuthenticatorsSectionId;

export const SECTION_LABELS: Record<SettingsSectionId, string> = {
  overview: "Overview",
  methods: "Methods",
  passkeys: "Passkeys",
  phone: "Phone & remote",
  recovery: "Recovery",
  sessions: "Sessions",
  models: "Approved models",
  "add-model": "Add a model",
  checks: "Identity checks",
};

export type AuthField = keyof AuthenticationSettings;

/**
 * Which tenant-settings fields each Sign-in section saves. Every field the
 * Sign-in tab edits belongs to exactly one section; `enforce_attestation` is
 * edited only on Authenticators › Approved models.
 */
export const SECTION_FIELDS = {
  methods: [
    "passkey_required",
    "auth_methods",
    "badge_signin_enabled",
    "badge_signin_provider_id",
    "badge_signin_label",
    "badge_signin_kiosk_mode",
  ],
  passkeys: [
    "enrollment_transports",
    "user_verification",
    "max_passkeys_per_user",
    "max_synced_passkeys_per_user",
    "max_device_bound_passkeys_per_user",
    "max_security_keys_per_user",
    "max_workstation_logon_credentials_per_user",
  ],
  phone: ["mobile_authenticator_enabled", "allow_remote_desktop_relay"],
  sessions: ["session_lifetime_minutes", "admin_idle_timeout_minutes", "audit_retention_days"],
} as const satisfies Partial<Record<SignInSectionId, readonly AuthField[]>>;

export type FieldOwningSection = keyof typeof SECTION_FIELDS;

/** Every field some Sign-in section saves. Anything else on the form
 *  (`enforce_attestation`, the settings the console no longer offers) is
 *  carried at its server value and never edited here. */
export const SIGN_IN_OWNED_FIELDS: ReadonlySet<AuthField> = new Set<AuthField>(
  Object.values(SECTION_FIELDS).flat(),
);

/** What each Sign-in section shows for this tenant. null = the section is
 *  absent. */
export interface SignInAvailability {
  /** Delegation banners on the Overview (old page header banners). */
  perOrgBanner: boolean;
  infrastructureBanner: boolean;
  /** Always present: the badge button renders for every shape. */
  methods: {
    /** Fallback-method list (old "Sign-in methods" card). */
    fallbacks: boolean;
    /** "Passkey only" (old "Require passkey on every sign-in"). */
    passkeyRequired: boolean;
    tenantDefaultPolicy: boolean;
    consumerPopulation: boolean;
  };
  passkeys: {
    enrollment: boolean;
    /** The phone option title (a wording slot), inside enrollment. */
    phoneOptionTitle: boolean;
    verification: boolean;
    limits: boolean;
    approvedModels: boolean;
  } | null;
  /** Who-may-create-an-account note: rendered with the old passkey policy
   *  card; it now sits in Methods, or in Passkeys where Methods is absent. */
  trustNote: boolean;
  phone: {
    /** QR, Bluetooth, number match and push (old Phone sign-in card). */
    policy: boolean;
  };
  recovery: {
    /** Support contact and help link (wording slots). */
    supportWording: boolean;
  };
  presets: boolean;
  sessions: {
    userSessions: boolean;
    perOrgSessionLabel: boolean;
  };
}

type CanFn = (permission: string) => boolean;

export function signInAvailability(
  tenant: Pick<TenantDetail, "customer_audience">,
  caps: TenantCapabilities,
  can: CanFn,
): SignInAvailability {
  const factorList = caps.managesUserFactors && !caps.hasOrganizationIdentityPartitions;
  const methods = {
    fallbacks: factorList,
    passkeyRequired: caps.performsPasskeyCeremony && caps.managesUserFactors,
    tenantDefaultPolicy:
      caps.hasOrganizationIdentityPartitions &&
      tenant.customer_audience !== "individual_customers" &&
      can("auth_methods.read"),
    consumerPopulation: tenant.customer_audience === "individual_customers",
  };
  const passkeys = {
    enrollment: caps.hostsEnrollmentUI,
    phoneOptionTitle: caps.hostsEnrollmentUI && caps.hostsLoginUI,
    verification: caps.performsPasskeyCeremony,
    limits: caps.performsPasskeyCeremony,
    approvedModels: caps.managesAttestation,
  };
  return {
    perOrgBanner: !caps.managesUserFactors && caps.hasPerOrgFederation,
    infrastructureBanner: !caps.managesUserFactors && caps.isInfrastructure,
    methods,
    passkeys: Object.values(passkeys).some(Boolean) ? passkeys : null,
    trustNote: caps.performsPasskeyCeremony,
    phone: { policy: factorList },
    recovery: { supportWording: caps.hostsLoginUI },
    presets: factorList,
    sessions: {
      userSessions: caps.issuesUserSessions,
      perOrgSessionLabel: caps.hasPerOrgFederation,
    },
  };
}

/** The Sign-in sections in nav order, present ones only. Overview, Methods
 *  (the badge button renders for every shape), Phone & remote (the app and
 *  relay switches render for every shape), Recovery and Sessions are always
 *  present. */
export function presentSignInSections(av: SignInAvailability): SignInSectionId[] {
  const out: SignInSectionId[] = ["overview", "methods"];
  if (av.passkeys) out.push("passkeys");
  out.push("phone", "recovery", "sessions");
  return out;
}

/**
 * Capability-only presence, for a child route's beforeLoad redirect. The
 * permission-dependent Methods card (tenant default policy) never decides
 * presence on its own: a customer tenant always has "Passkey only".
 */
export function signInSectionPresent(
  section: SignInSectionId,
  tenant: Pick<TenantDetail, "customer_audience">,
  caps: TenantCapabilities,
): boolean {
  return presentSignInSections(signInAvailability(tenant, caps, () => true)).includes(section);
}

/**
 * The section a pathname opens, for naming the destination of a blocked
 * navigation. Null for anything outside the two Authentication tabs'
 * sections.
 */
export function sectionForPath(pathname: string): SettingsSectionId | null {
  const m = pathname.match(/\/authentication\/(sign-in|authenticators)(?:\/([^/?#]+))?\/?$/);
  if (!m) return null;
  const [, tab, child] = m;
  if (tab === "sign-in") {
    if (!child) return "overview";
    const ids: SignInSectionId[] = ["methods", "passkeys", "phone", "recovery", "sessions"];
    return ids.find((id) => id === child) ?? null;
  }
  if (!child) return "models";
  if (child === "new") return "add-model";
  if (child === "identity-checks") return "checks";
  return null;
}

export function sectionLabelForPath(pathname: string): string | null {
  const section = sectionForPath(pathname);
  return section ? SECTION_LABELS[section] : null;
}
